import { useAtomValue, useSetAtom } from "jotai";
import {
  remittancesAtom,
  currentPageAtom,
  errorAtom,
  isLoadingAtom,
  pageLimitAtom,
  totalCountAtom,
  getRemittancesAtom,
  currentExpandedRemittanceKeyAtom,
  setCurrentPageAtom,
  setCurrentExpandedRemittanceKeyAtom,
  setSortColumnAtom,
  sortColumnAtom,
  downloadRemittanceDocumentsAtom,
} from "@/store/deduction/remittance";
import { Remittance } from "@/core/entities/deduction/remittance";
import { ITableSortColumn } from "@/core/interface";
import { searchRemittanceAtom, setSearchRemittanceAtom } from "@/store/deduction/main";
import { IDownloadRemittanceDocumentsRepositoryResponse } from "@/core/repositories/remittance";

interface UseRemittance {
  remittances: Remittance[];
  currentExpandedRemittanceKey: number | null;
  isLoading: boolean;
  error: Error | null;
  totalCount: number;
  currentPage: number;
  pageLimit: number;
  totalPages: number;
  currentSortColumn: ITableSortColumn | null;
  triggerGetRemittancesByCheck: (refetch?: boolean) => void;
  handlePageChange: (page: number) => void;
  resetPageNumber: () => void;
  handleSortColumnChange: (sortColumn: ITableSortColumn | null) => void;
  handleExpandRemittance: (key: number) => void;
  searchRemittance: string;
  handleSearchRemittance: (searchRemittance: string) => void;
  downloadRemittanceDocuments: (remittanceId: number, checkNumber: string, formats: { date?: string; number?: string }) => Promise<IDownloadRemittanceDocumentsRepositoryResponse | string>;
}

export function useRemittance(): UseRemittance {
  // Read-only atoms
  const remittances = useAtomValue(remittancesAtom);
  const isLoading = useAtomValue(isLoadingAtom);
  const totalCount = useAtomValue(totalCountAtom);
  const currentPage = useAtomValue(currentPageAtom);
  const currentSortColumn = useAtomValue(sortColumnAtom);
  const pageLimit = useAtomValue(pageLimitAtom);
  const currentExpandedRemittanceKey = useAtomValue(currentExpandedRemittanceKeyAtom);
  const error = useAtomValue(errorAtom);
  const searchRemittance = useAtomValue(searchRemittanceAtom);
  const totalPages = Math.ceil(totalCount / pageLimit);

  // Action atoms
  const getRemittances = useSetAtom(getRemittancesAtom);
  const setCurrentPage = useSetAtom(setCurrentPageAtom);
  const setSortColumn = useSetAtom(setSortColumnAtom);
  const setCurrentExpandedRemittanceKey = useSetAtom(setCurrentExpandedRemittanceKeyAtom);
  const setSearchRemittance = useSetAtom(setSearchRemittanceAtom);

  // Get remittances by check for the first time or refetch
  const triggerGetRemittancesByCheck = (refetch?: boolean) => {
    if (refetch) {
      setCurrentPage(1);
    }
    getRemittances(refetch);
  };

  // Handle page change event
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    getRemittances();
  };

  const resetPageNumber = () => {
    setCurrentPage(1);
  };

  // Handle sort column change event
  const handleSortColumnChange = (sortColumn: ITableSortColumn | null) => {
    setSortColumn(sortColumn);
    getRemittances();
  };

  // Handle expand remittance event
  const handleExpandRemittance = (key: number) => {
    setCurrentExpandedRemittanceKey(key);
  };

  // Handle search remittance event
  const handleSearchRemittance = (value: string) => {
    setSearchRemittance(value);
    handlePageChange(1);
  };

  const downloadRemittanceDocuments = useSetAtom(downloadRemittanceDocumentsAtom);

  return {
    remittances,
    currentExpandedRemittanceKey,
    isLoading,
    error,
    totalCount,
    currentPage,
    currentSortColumn,
    pageLimit,
    totalPages,
    triggerGetRemittancesByCheck,
    handlePageChange,
    resetPageNumber,
    handleSortColumnChange,
    handleExpandRemittance,
    searchRemittance,
    handleSearchRemittance,
    downloadRemittanceDocuments,
  };
}
